import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck, FileText, Clock, Sparkles, PenTool, Users,
  Building2, ArrowRight, CheckCircle2, Lock, Volume2, VolumeX, TrendingUp,
} from "lucide-react";

export default function Landing() {
  const { user } = useAuth();
  const [brand, setBrand] = useState({ company_name: "D&Z Management" });
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (!v.muted) v.play().catch(() => {});
  };

  useEffect(() => {
    api.get("/public/branding").then((r) => setBrand(r.data)).catch(() => {});
    const v = videoRef.current;
    if (v) v.play().catch(() => {});
  }, []);

  const homeFor = user ? (user.role === "client" ? "/portal" : user.role === "diy" ? "/diy" : "/admin") : "/login";

  return (
    <div className="min-h-screen bg-[#F8FAF8]" data-testid="landing-page">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            {brand.logo_url
              ? <img src={brand.logo_url} alt={brand.company_name} className="h-9 w-9 rounded object-contain" />
              : <ShieldCheck className="h-8 w-8 text-[#0B2545]" />}
            <span className="font-heading font-extrabold text-xl text-[#0B2545] tracking-tight" data-testid="landing-brand">{brand.company_name}</span>
          </div>
          <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-600">
            <a href="#how" className="hover:text-[#0B2545] transition-colors duration-150">How it works</a>
            <a href="#services" className="hover:text-[#0B2545] transition-colors duration-150">Services</a>
            <a href="#partners" className="hover:text-[#0B2545] transition-colors duration-150">For businesses</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to={user ? homeFor : "/login"} data-testid="landing-login-btn"
              className="text-sm font-medium text-slate-600 hover:text-[#0B2545] px-3 py-2 transition-colors duration-150">
              {user ? "Open my dashboard" : "Sign in"}
            </Link>
            <Link to="/register">
              <Button data-testid="landing-signup-btn" className="bg-[#0B2545] hover:bg-[#134074] text-white font-semibold">Get started</Button>
            </Link>
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-4 pt-14 pb-16 grid lg:grid-cols-2 gap-10 items-center">
        <div className="space-y-5 order-first">
          <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-xl bg-slate-950">
            <video ref={videoRef} autoPlay muted loop playsInline preload="auto" src="/dz-talking-video.mp4"
              poster="/dz-talking-v2-frame.jpg"
              className="w-full" data-testid="landing-welcome-video" />
            <button onClick={toggleSound} data-testid="landing-video-sound-btn"
              className="absolute bottom-3 right-3 rounded-full bg-white/90 hover:bg-white text-slate-900 text-xs font-semibold px-3 py-1.5 flex items-center gap-1.5 transition-colors duration-150">
              {muted
                ? <><VolumeX className="h-3.5 w-3.5" /> Tap for sound</>
                : <><Volume2 className="h-3.5 w-3.5" /> Sound on</>}
            </button>
          </div>
          <ScoreRiseCard />
        </div>
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-xs font-semibold text-emerald-800 mb-5">
            <Sparkles className="h-3.5 w-3.5" /> AI-powered credit repair, human-run disputes
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
            Welcome to Your Credit Comeback — better credit starts here.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
            We find the negative items dragging your score down, dispute them with all three bureaus by certified mail, and track every 30-day legal deadline — while you watch it happen from your own portal.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register">
              <Button size="lg" data-testid="landing-hero-cta" className="bg-[#0B2545] hover:bg-[#134074] text-white font-semibold px-7">
                Start my comeback <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
            <a href="#services">
              <Button size="lg" variant="outline" data-testid="landing-diy-btn" className="border-slate-300">
                Just the letters — $99 DIY
              </Button>
            </a>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-500">
            <span className="flex items-center gap-2"><FileText className="h-4 w-4 text-[#0B2545]" /> 4 dispute rounds + FDCPA</span>
            <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-[#0B2545]" /> 30-day bureau clock tracked</span>
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#0B2545]" /> Certified mail, always</span>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 pb-16">
        <div className="rounded-2xl overflow-hidden border border-slate-200 grid md:grid-cols-2 bg-white shadow-lg" data-testid="laptop-scores-section">
          <img src="/dz-laptop-scores.jpeg" alt="Laptop showing credit scores rising from 550 to 700 across Equifax, Experian, and TransUnion" className="w-full h-full object-cover min-h-64" data-testid="laptop-scores-image" />
          <div className="p-8 flex flex-col justify-center">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-3">From 550 to 700 — across all three bureaus.</h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-4">We dispute with Equifax, Experian, and TransUnion at the same time. Your portal tracks every score move, every deletion, and every 30-day deadline — all in one place.</p>
            <div className="flex flex-wrap gap-2 text-xs font-semibold">
              <span className="rounded-full bg-slate-100 border border-slate-200 px-3 py-1 text-slate-700">Equifax</span>
              <span className="rounded-full bg-slate-100 border border-slate-200 px-3 py-1 text-slate-700">Experian</span>
              <span className="rounded-full bg-slate-100 border border-slate-200 px-3 py-1 text-slate-700">TransUnion</span>
            </div>
          </div>
        </div>
      </section>

      <section id="how" className="bg-white border-y border-slate-200 py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-2">How your comeback works</h2>
          <p className="text-slate-500 mb-10 max-w-2xl">Three steps. No jargon. You always know exactly where things stand.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              ["1", "Tell us your story", "A 2-minute intake, then upload your ID and credit reports to your encrypted vault. Our AI reads every line and flags the items hurting you."],
              ["2", "We dispute — by certified mail", "We draft FCRA-compliant letters, you approve, and they go out to Equifax, Experian, and TransUnion with USPS tracking. The 30-day legal clock starts ticking."],
              ["3", "Watch items fall off", "Your portal shows every dispute, every deadline, every deletion. Bureaus miss their window? We escalate — Round 2, 3, and 4 letters until it's resolved."],
            ].map(([n, t, d]) => (
              <div key={n} className="rounded-xl border border-slate-200 bg-[#F8FAFC] p-6">
                <div className="h-10 w-10 rounded-lg bg-[#0B2545] text-white font-heading font-bold flex items-center justify-center mb-4">{n}</div>
                <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">{t}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="services" className="max-w-6xl mx-auto px-4 py-16">
        <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-10">Two ways to fix your credit</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border-2 border-[#0B2545] bg-white p-7">
            <div className="flex items-center gap-2 mb-3">
              <Users className="h-5 w-5 text-[#0B2545]" />
              <h3 className="font-heading font-bold text-xl text-slate-900">Full service</h3>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed mb-5">Our case managers handle everything — report analysis, dispute strategy, certified mailing, follow-up rounds, and bureau escalation. You get a portal with live progress and a human on your side.</p>
            <ul className="space-y-2 text-sm text-slate-700 mb-6">
              {["Dedicated case manager", "AI report analysis + human review", "Certified mail with tracking, all rounds", "Secure document vault & e-sign"].map((f) => (
                <li key={f} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />{f}</li>
              ))}
            </ul>
            <Link to="/register"><Button data-testid="landing-fullservice-btn" className="w-full bg-[#0B2545] hover:bg-[#134074] text-white font-semibold">Get started</Button></Link>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-7">
            <div className="flex items-center gap-2 mb-3">
              <PenTool className="h-5 w-5 text-[#0B2545]" />
              <h3 className="font-heading font-bold text-xl text-slate-900">DIY dispute letters</h3>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed mb-5">Just want the letters? Add your items (or let AI scan your report), pick a template, pay per letter, and download a bureau-ready PDF with the mailing address and certified-mail instructions.</p>
            <ul className="space-y-2 text-sm text-slate-700 mb-6">
              {["$99 per professional letter", "All 4 dispute rounds + FDCPA validation", "AI scans your report for you", "Bureau addresses & instructions included"].map((f) => (
                <li key={f} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />{f}</li>
              ))}
            </ul>
            <Link to="/register"><Button variant="outline" data-testid="landing-diy-signup-btn" className="w-full border-slate-300">Generate my first letter</Button></Link>
            <p className="text-xs text-slate-400 mt-3 text-center">All DIY letter sales are final.</p>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 pb-16">
        <div className="rounded-2xl overflow-hidden border border-slate-200 grid md:grid-cols-2 bg-white">
          <img src="/dz-office-interior.jpeg" alt="The D&Z Management office" className="w-full h-full object-cover min-h-64" />
          <div className="p-8 flex flex-col justify-center">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-3">A real office. Real accountability.</h2>
            <p className="text-sm text-slate-600 leading-relaxed mb-4">Every action on your file is logged. Your documents are encrypted in transit and at rest. And under the Credit Repair Organizations Act, you can cancel within 3 business days of signing — no questions asked.</p>
            <div className="flex items-center gap-2 text-sm text-slate-500"><Lock className="h-4 w-4 text-emerald-600" /> Bank-grade encryption · full audit trail</div>
          </div>
        </div>
      </section>

      <section id="partners" className="bg-[#0B2545] text-white py-16">
        <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Building2 className="h-5 w-5 text-sky-300" />
              <span className="text-xs font-mono uppercase tracking-[0.18em] text-sky-300">For businesses</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-3">Run credit repair under your own brand</h2>
            <p className="text-sky-100 text-sm leading-relaxed max-w-lg">Tax pros, realtors, and entrepreneurs: get the entire platform — CRM, AI report analysis, dispute engine, certified mail, billing, client portal — wearing your logo. Standalone at $275 + $25/mo, or included in the Dynamic Tax bundle.</p>
          </div>
          <div className="md:justify-self-end">
            <Link to="/agency-signup?ref=dzmanagement-site">
              <Button size="lg" data-testid="landing-agency-btn" className="bg-white text-[#0B2545] hover:bg-sky-50 font-semibold px-7">
                Get a white-label workspace <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white py-8">
        <div className="max-w-6xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400">
          <span>© {new Date().getFullYear()} {brand.company_name} · dzmanagement.site</span>
          <span>You have the right to dispute inaccurate information yourself, free of charge. Results are never guaranteed.</span>
        </div>
      </footer>
    </div>
  );
}

function Gauge({ score, label, sub, color }) {
  const pct = Math.min(1, Math.max(0, (score - 300) / 550));
  const arc = Math.PI * 60;
  return (
    <div className="text-center">
      <svg viewBox="0 0 140 84" className="w-full">
        <path d="M10 70 A60 60 0 0 1 130 70" fill="none" stroke="#E2E8F0" strokeWidth="12" strokeLinecap="round" />
        <path d="M10 70 A60 60 0 0 1 130 70" fill="none" stroke={color} strokeWidth="12" strokeLinecap="round"
          strokeDasharray={`${pct * arc} ${arc}`} />
        <text x="70" y="64" textAnchor="middle" fontSize="26" fontWeight="800" fill="#0F172A">{score}</text>
      </svg>
      <div className="text-xs font-bold mt-1" style={{ color }}>{label}</div>
      <div className="text-[10px] text-slate-400">{sub}</div>
    </div>
  );
}

function ScoreRiseCard() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-lg p-5" data-testid="score-rise-card">
      <div className="text-xs font-mono uppercase tracking-[0.18em] text-slate-400 text-center mb-3">A real credit comeback</div>
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
        <Gauge score={574} label="POOR" sub="Before D&Z" color="#DC2626" />
        <div className="flex flex-col items-center gap-1">
          <TrendingUp className="h-9 w-9 text-emerald-600" />
          <span className="rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-bold px-2.5 py-0.5">+207 pts</span>
        </div>
        <Gauge score={781} label="EXCELLENT" sub="After disputes" color="#059669" />
      </div>
      <p className="text-[10px] text-slate-400 text-center mt-3">Illustrative result. Outcomes vary — no specific score increase is guaranteed.</p>
    </div>
  );
}
